import React, { useMemo } from 'react';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import MenuIcon from '@mui/icons-material/MoreVert';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import VisibilityOnIcon from '@mui/icons-material/Visibility';
import { Alert, Box, Button, Divider, IconButton, List, Paper, Typography } from '@mui/material';
import { createSelector } from 'reselect';

import { PropertySelectField } from '@paragon/client-tools';
import { LayoutModel, Level } from '@paragon/contract-library';
import { getInsertionsWithPendingChanges, modelPropertyChanged, saveAllProjectChanges } from '@paragon/layout-models';
import { createScrollbarStyling } from '@paragon/themes';
import { naturalSortByProperty, notNullish, sortBy } from '@paragon/utilities';

import {
  ensureLevelIsVisible,
  hideLevelByGuid,
  showEntireBuilding,
} from '../../../../store/actions/levelVisibilityActions';
import { handleStandardSceneItemRightClick } from '../../../../store/actions/sceneInteractionActions';
import { selectItemByGuid } from '../../../../store/actions/selectActions';
import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import {
  doGhostObjectsExistInCurrentLayout,
  getActiveLayoutLevelGuidOrNull,
  getAllBuildingGuids,
  getAllBuildings,
  getAllLevels,
  getIsLayoutHierarchyPresentInProject,
  getLayoutHierarchyFilter,
  getVisibleBuildingGuidOrNull,
  getVisibleLevelGuids,
} from '../../../../store/selectors/layoutDataSelectors';
import { ghostObjectVisibilityEnabled } from '../../../../store/slices/selectionsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { AddBuildingButton } from '../navigation-panel/other-buttons/AddBuildingButton';
import { AddLevelButton } from '../navigation-panel/other-buttons/AddLevelButton';
import { LevelListItem } from './LevelListItem';

const getBuildingNames = createSelector(getAllBuildings, (buildings) =>
  Object.fromEntries(buildings.map((building) => [building.guid, building.name])),
);

export function LayoutHierarchyPanel(props: { onContextMenuDidOpen?: () => void; onClose?: () => void }) {
  const dispatch = useAppDispatch();

  const formatDistance = useAppSelector(getDistanceFormatterSelector);

  const clientContext = useAppSelector((state) => state.application.openContext);
  const inProjectContext = clientContext?.type === 'project';

  const buildingGuids = useAppSelector(getAllBuildingGuids);
  const buildingNames = useAppSelector(getBuildingNames);

  const layoutHierarchyPresent = useAppSelector(getIsLayoutHierarchyPresentInProject);

  const activeLevelGuid = useAppSelector(getActiveLayoutLevelGuidOrNull);
  const visibleLevelGuids = useAppSelector(getVisibleLevelGuids);

  const ghostObjectsPresent = useAppSelector(doGhostObjectsExistInCurrentLayout);

  const hierarchyFilter = useAppSelector(getLayoutHierarchyFilter);
  const activeBuildingGuid = useAppSelector(getVisibleBuildingGuidOrNull);

  const allLevels = useAppSelector(getAllLevels);
  const insertions = useAppSelector(getInsertionsWithPendingChanges);

  const levelsInActiveBuilding = useMemo(() => {
    if (activeBuildingGuid == null) return [];

    const buildingChildrenGuids = new Set(
      insertions
        .filter((insertion) => insertion.parentGuid === activeBuildingGuid)
        .map((insertion) => insertion.objectGuid),
    );

    return allLevels.filter((level) => buildingChildrenGuids.has(level.guid)) as (LayoutModel & Level)[];
  }, [activeBuildingGuid, allLevels, insertions]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const sortedLevels = sortBy(levelsInActiveBuilding, (level) => level.index, 'desc');

  const hierarchyGrid =
    levelsInActiveBuilding.length > 0 ? (
      <List
        dense
        sx={[
          createScrollbarStyling,

          {
            height: 300,
            overflowY: 'auto',
          },
        ]}
      >
        <DndContext
          sensors={sensors}
          modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
          collisionDetection={closestCenter}
          onDragEnd={async ({ active, over }) => {
            if (!over || active.id === over.id) return;

            const oldIndex = sortedLevels.findIndex((item) => item.guid === active.id);
            const newIndex = sortedLevels.findIndex((item) => item.guid === over.id);

            const reorderedLevels = [...sortedLevels];
            const [movedLevel] = reorderedLevels.splice(oldIndex, 1);
            reorderedLevels.splice(newIndex, 0, movedLevel);

            const modelsToUpdate = reorderedLevels
              .map((level, position) => {
                const newIndex = reorderedLevels.length - 1 - position;
                return level.index !== newIndex ? { guid: level.guid, newIndex } : null;
              })
              .filter(notNullish);

            for (const { guid, newIndex } of modelsToUpdate) {
              dispatch(
                modelPropertyChanged({
                  modelGuids: [guid],
                  propertyName: 'index',
                  newValue: newIndex,
                }),
              );
            }

            await dispatch(saveAllProjectChanges());
          }}
        >
          <SortableContext items={sortedLevels.map((item) => item.guid)} strategy={verticalListSortingStrategy}>
            {sortedLevels.map((level) => {
              const visible = visibleLevelGuids.has(level.guid);

              let displayName = /^\d+$/.test(level.name) ? `Level ${level.name}` : level.name;

              if (level.plateElevation > 0) displayName += ` — ${formatDistance(level.plateElevation)}`;

              function toggleVisibility() {
                if (visible) dispatch(hideLevelByGuid(level.guid));
                else dispatch(ensureLevelIsVisible(level.guid));
              }

              return (
                <LevelListItem
                  key={level.guid}
                  level={level}
                  displayName={displayName}
                  active={activeLevelGuid != null && level.guid === activeLevelGuid}
                  visible={visible}
                  toggleVisibility={toggleVisibility}
                  onContextMenuDidOpen={props.onContextMenuDidOpen}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </List>
    ) : null;

  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={[{ paddingX: 2, paddingY: 1, overflowY: 'scroll' }, createScrollbarStyling]}
    >
      {layoutHierarchyPresent && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <PropertySelectField
            property="Active Building"
            options={naturalSortByProperty(Array.from(buildingGuids), (guid) => buildingNames[guid] ?? guid)}
            getName={(guid) => buildingNames[guid] ?? guid}
            value={activeBuildingGuid}
            submit={(guid) => {
              dispatch(selectItemByGuid(guid, 'building'));
            }}
          />

          {hierarchyFilter !== 'ghost' && inProjectContext && (
            <IconButton
              sx={{ marginLeft: 1 }}
              onClick={async (event) => {
                if (activeBuildingGuid == null) return;

                const menuDidOpen = await dispatch(
                  handleStandardSceneItemRightClick(event, {
                    type: 'building',
                    guid: activeBuildingGuid,
                    origin: 'dom',
                  }),
                );

                if (menuDidOpen && props.onContextMenuDidOpen) {
                  props.onContextMenuDidOpen();
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>
      )}

      {(hierarchyFilter !== 'ghost' || !layoutHierarchyPresent) && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            '&>*': { flexGrow: 1 },
            marginBottom: 1,
          }}
        >
          <AddBuildingButton />
          <AddLevelButton />
        </Box>
      )}

      {hierarchyFilter !== 'ghost' && layoutHierarchyPresent && (
        <>
          <Divider />

          <Button
            fullWidth
            variant="outlined"
            sx={{ marginY: 1 }}
            startIcon={<VisibilityOnIcon />}
            onClick={() => {
              dispatch(showEntireBuilding());
            }}
          >
            Show entire building
          </Button>

          {hierarchyGrid}

          <Divider sx={{ marginBottom: 1 }} />
        </>
      )}

      {layoutHierarchyPresent &&
        ghostObjectsPresent &&
        (hierarchyFilter === 'ghost' ? (
          <Alert variant="outlined" severity="warning" sx={{ marginBottom: 1, paddingY: 0.5 }}>
            Currently showing objects that do not belong to any building. To exit this mode, choose a level to make
            visible above.
          </Alert>
        ) : (
          <>
            <Alert variant="outlined" severity="warning" sx={{ marginBottom: 1, paddingY: 0.5 }}>
              Some objects do not belong to any building.
            </Alert>

            <Button
              startIcon={<TroubleshootIcon />}
              variant="outlined"
              fullWidth
              sx={{ marginBottom: 1 }}
              onClick={() => {
                if (props.onClose) props.onClose();
                dispatch(ghostObjectVisibilityEnabled());
              }}
            >
              Show objects not in buildings
            </Button>
          </>
        ))}

      {!layoutHierarchyPresent && inProjectContext && (
        <>
          <Divider sx={{ marginBottom: 1 }} />

          <Typography variant="body1" color="textSecondary" sx={{ marginX: 2 }}>
            Buildings and levels are not required in a layout. If you&apos;d like to use them, add a building to get
            started.
          </Typography>
        </>
      )}
    </Paper>
  );
}
